<script setup>
import { getDuration, getPercentage, getDeclOfNum } from '@/utils';
import { ref, computed, nextTick } from 'vue';
import { Info, Cross, Arrow } from '@/components/Icons';
import ServiceContent from './Content/Service.vue';
import MasterContent from './Content/Master.vue';
import { useMainStore } from '@/store/main';

const emit = defineEmits(['update:item', 'switchService']);

const selectedService = defineModel('service');
const selectedMaster  = defineModel('master');

const mainStore = useMainStore()

const contentComponents = {
	Service: ServiceContent,
	Master: MasterContent
};

const dynamicContent = computed(() => {
	return contentComponents[props.type] || null;
});

const props = defineProps({
	id          : Number,
	type        : String,
	title       : String,
	cardId      : Number,
	filterValue : Array,
});

const expandedItem    = ref(null);
const activeItem      = ref(null);
const activePanel     = ref(null);

// панель с кнопкой «Далее» рендерится под длинным списком и на телефоне уходит за экран
const selectItem = item => {
	activeItem.value = item;
	nextTick(() => activePanel.value?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
};

const serviceItem = computed(() =>
	selectedService.value || activeItem.value
);

const masterItem = computed(() =>
	selectedMaster.value || activeItem.value
);

const serviceImg = computed(() =>
	`/mediafiles/${serviceItem.value.img}`
);

const itemImgSrc = item => {
	return item.img ? `/mediafiles/${item.img}` : '/staticfiles/vue/public/profile.png';
};

const reviewsText = computed(() =>
	getDeclOfNum(activeItem.value.reviews_count, ['отзыв', 'отзыва', 'отзывов'])
);

const getPrice = price => price.toLocaleString();

const setDescriptionBlock = id => expandedItem.value = expandedItem.value == id ? null : id;

const updateItem = () => {
	if (props.id == 0)
		selectedService.value = activeItem.value;
	else if (props.id == 1)
		selectedMaster.value = activeItem.value;

	emit('update:item', { id: props.id, value: activeItem.value.id });

	activeItem.value   = null;
	expandedItem.value = null;
};

const orderedServices = computed(() => {
	const servicesArray = [];
	mainStore.formData[props.cardId].drafts.forEach(el => servicesArray.push(...el.services.map(elem => elem.id)));
	return servicesArray;
})

const switchService = () => {
	emit('switchService');

	selectedService.value = null;
	expandedItem.value = null;
	activeItem.value = null;
};
</script>

<template>
	<div class="dropdown-content">
		<div class="dropdown-content-wr">
			<div class="dropdown-content__header">
				{{ title }}
			</div>
			<ul class="dropdown-content__content">
				<li
					class="dropdown-content__item"
					v-for="item in filterValue"
					:key="item.id"
					:class="{'dropdown-content__item--active': activeItem?.id == item.id}"
				>
					<button
						class="dropdown-content__btn"
						v-if="type != 'Service' || orderedServices.indexOf(item.id) == -1"
						@click="selectItem(item)"
					>
						<div class="dropdown-content__left">
							<img
								alt="Фото"
								v-if="type == 'Master'"
								:src="itemImgSrc(item)"
							/>
							<span>{{ item.title || item.fullname }}</span>
						</div>
						<div class="dropdown-content__right">
							<span v-if="item.price"
								>{{ getPrice(item.price) }} ₽</span
							>
							<button
								class="dropdown-content__info"
								@click.stop="setDescriptionBlock(item.id)"
							>
								<Info v-if="expandedItem != item.id" />
								<Cross v-else />
							</button>
						</div>
					</button>
					<div class="dropdown-accordion" v-if="expandedItem == item.id">
						<component :is="dynamicContent" :item />
					</div>
				</li>
			</ul>
			<div class="dropdown-content__active-item-wr" ref="activePanel" v-if="serviceItem">
				<div
					class="dropdown-content__active-item service-item"
					v-if="serviceItem"
				>
					<img :src="serviceImg" alt="" />
					<h3>{{ serviceItem.title }}</h3>
					<span>{{ getDuration(serviceItem.duration) }}</span>
					<button
						class="dropdown-content__back-btn"
						v-if="selectedService"
						@click="switchService"
					>
						<Arrow />
					</button>
				</div>
				<div
					class="dropdown-content__active-item master-item"
					v-if="masterItem && selectedService"
				>
					<img :src="itemImgSrc(masterItem)" alt="" />
					<h3>{{ masterItem.fullname }}</h3>
					<div class="review__rating">
						<span>{{ masterItem.average_rating }}</span>
						<div
							class="master-wr__stars"
							:aria-label="`Рейтинг: ${masterItem.average_rating} из 5`"
						>
							<span
								class="master-wr__progress-bar"
								:style="{ width: getPercentage(masterItem.average_rating, 5) + '%' }"
							/>
						</div>
						<span>({{ reviewsText }})</span>
					</div>
				</div>
				<button v-if="serviceItem && masterItem" @click="updateItem">Далее</button>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.dropdown-content__item {
	&--active .dropdown-content__btn { background-color: $softYellow }

	& + & .dropdown-content__btn { border-top: 1px solid #d9d9d9 }

	span { text-align: left }
}

.dropdown-content__btn {
	width: 100%;
	font-size: 14px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 15px 16px;

	.dropdown-content__left { max-width: 180px }

	& > div {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	img {
		width: 39px;
		height: 39px;
		border-radius: 8px;
	}
}

.dropdown-content__info {
	width: 20px;
	height: 20px;
}

.dropdown-content__right span {
	font-family: 'Roboto', 'Raleway';
	font-size: 14px;
	color: #292f3d;
	line-height: 29px;
}

.dropdown-accordion {
	overflow: hidden;
	padding: 24px 16px;
	background-color: #fff;
	box-shadow: 0 0 14px 0 #00000014 inset;
}

.dropdown-content__active-item {
	width: fit-content;
	display: grid;
	grid-template-rows: repeat(2, auto);
	grid-template-columns: repeat(2, auto);
	gap: 4px 14px;
	font-family: 'Raleway';
	font-weight: 500;

	.master-wr__stars { margin-inline: 4px 8px }

	& + & { margin-top: 12px }

	h3 {
		font-size: 16px;
		font-family: 'Raleway';
		font-weight: 600;
	}

	span { font-size: 12px }

	img {
		width: 48px;
		height: 48px;
		grid-row: 1 / 3;
		border-radius: 8px;
	}
}

.dropdown-accordion__content {
	margin-top: 16px;
	font-size: 14px;
}

.dropdown-content__active-item-wr {
	width: 100%;
	position: relative;
	padding: 20px 16px;
	box-shadow: 0 4px 20px 0 #00000026;
	background-color: #fff;

	& > button {
		width: 100%;
		height: 44px;
		margin-top: 12px;
		font-size: 18px;
		font-family: 'Raleway';
		font-weight: 600;
		background-color: $yellow;
		color: #fff;
		border-radius: 8px;
	}
}

.dropdown-content__back-btn {
	width: 20px;
	height: 20px;
	position: absolute;
	right: 16px;

	svg { transform: rotate(90deg) }
}

.master-wr__stars {
	width: 60px;
	height: 12px;
	display: flex;
	align-items: center;
	background-color: #f9f9f9;
	mask-image: url('public/icons/star.svg');
}

.master-wr__progress-bar {
	height: 100%;
	background-color: $yellow;
}

.master-wr__reviews {
	display: flex;
	align-items: center;
	font-variant-numeric: lining-nums;

	.master-wr__stars { margin-inline: 4px 8px }

	span { font-size: 12px }
}

.review__rating {
	height: 20px;
	display: flex;
	align-items: center;
}
</style>
